import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';
import Layout from '@/Layout';
import ActivityPage from '@/pages/Activity';
import ForgotPasswordPage from '@/pages/ForgotPassword';
import LoginPage from '@/pages/Login';
import MailPage from '@/pages/Mail';
import OrderDetailsPage from '@/pages/OrderDetails';
import EasyPostPage from '@/pages/EasyPost';
import ReportsPage from '@/pages/Reports';
import ResetPasswordPage from '@/pages/ResetPassword';
import ShippingPage from '@/pages/Shipping';
import UploadPage from '@/pages/Upload';
import SettingsPage from '@/pages/Settings';

function FullPageSpinner() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );
}

export default function App() {
  // The single source of truth for "is anyone signed in". Asking our own API
  // rather than reading a cookie means a revoked session is noticed on the next
  // load, not whenever the cookie happens to expire.
  const { data: user, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <FullPageSpinner />;

  return (
    <BrowserRouter>
      {user ? <AuthenticatedRoutes user={user} /> : <UnauthenticatedRoutes />}
    </BrowserRouter>
  );
}

function UnauthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Everything else bounces to sign-in rather than rendering an empty shell. */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function AuthenticatedRoutes({ user }) {
  return (
    <Layout user={user}>
      <Routes>
        <Route path="/" element={<Navigate to="/mail" replace />} />
        <Route path="/login" element={<Navigate to="/mail" replace />} />
        {/* Reachable while signed in: following a reset link with a live session
            should still let you set the password, not bounce to the app. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Admin only. Without the route, a staff member who types /settings or
            has it bookmarked falls through to the catch-all below and lands on
            Mail, rather than on an empty page. */}
        {user.role === 'admin' && (
          <Route path="/settings" element={<SettingsPage user={user} />} />
        )}
        <Route path="/mail" element={<MailPage />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/shipping" element={<ShippingPage />} />
        <Route path="/orders/:id" element={<OrderDetailsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        {user.role === 'admin' && <Route path="/activity" element={<ActivityPage />} />}
        {user.role === 'admin' && <Route path="/easypost" element={<EasyPostPage />} />}

        <Route path="*" element={<Navigate to="/mail" replace />} />
      </Routes>
    </Layout>
  );
}
